/* Fijnmazige push-preferences: level + stille uren.
 * - level: all | schriftje_only | important | none
 * - quiet_hours: null of {from,to}
 */
import { useEffect, useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BellRing, Moon } from "lucide-react";
import { getPushPreferences, setPushPreferences } from "@/lib/push";

const LEVEL_OPTIONS = [
  { value: "all", label: "Alles", desc: "Elke update: schriftje, daglog, berichten." },
  { value: "important", label: "Alleen belangrijk", desc: "Schriftje-dagverslagen + directe berichten." },
  { value: "schriftje_only", label: "Alleen schriftje", desc: "Uitsluitend het dagverslag." },
  { value: "none", label: "Geen meldingen", desc: "In-app blijft zichtbaar, geen push." },
];

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export default function PushPreferences() {
  const [level, setLevel] = useState("all");
  const [quietOn, setQuietOn] = useState(false);
  const [quietFrom, setQuietFrom] = useState("20:00");
  const [quietTo, setQuietTo] = useState("07:00");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const p = await getPushPreferences();
      setLevel(p.level || "all");
      if (p.quiet_hours && p.quiet_hours.from && p.quiet_hours.to) {
        setQuietOn(true);
        setQuietFrom(p.quiet_hours.from);
        setQuietTo(p.quiet_hours.to);
      } else {
        setQuietOn(false);
      }
      setDirty(false);
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    if (quietOn && (!HHMM_RE.test(quietFrom) || !HHMM_RE.test(quietTo))) {
      toast.error("Stille uren moeten HH:MM zijn (bijv. 20:00 en 07:00)");
      return;
    }
    setBusy(true);
    try {
      await setPushPreferences({
        level,
        quiet_hours: quietOn ? { from: quietFrom, to: quietTo } : null,
      });
      setDirty(false);
      toast.success("Voorkeuren opgeslagen");
    } catch (e) {
      toast.error("Opslaan mislukt");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-4 text-sm text-neutral-500">Laden…</div>;

  return (
    <div className="p-4 space-y-4" data-testid="push-preferences">
      <div>
        <div className="flex items-center gap-2 mb-2 text-sm font-medium text-neutral-800">
          <BellRing className="h-4 w-4 text-[color:var(--sage)]" />
          Welke pushes ontvang je?
        </div>
        <RadioGroup
          value={level}
          onValueChange={(v) => { setLevel(v); setDirty(true); }}
          className="space-y-2"
        >
          {LEVEL_OPTIONS.map(opt => (
            <div key={opt.value} className="flex items-start gap-3 p-2 rounded-md hover:bg-neutral-50">
              <RadioGroupItem value={opt.value} id={`push-level-${opt.value}`}
                data-testid={`push-level-${opt.value}`} className="mt-0.5" />
              <label htmlFor={`push-level-${opt.value}`} className="flex-1 cursor-pointer">
                <div className="text-sm font-medium text-neutral-800">{opt.label}</div>
                <div className="text-[11px] text-neutral-500 leading-tight">{opt.desc}</div>
              </label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="pt-3 border-t border-neutral-100">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-md flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)]">
            <Moon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-neutral-800">Stille uren</div>
            <div className="text-[11px] text-neutral-500">Onderdruk pushes tijdens rust. In-app blijft zichtbaar.</div>
          </div>
          <Switch
            checked={quietOn}
            onCheckedChange={(v) => { setQuietOn(v); setDirty(true); }}
            data-testid="push-quiet-toggle"
          />
        </div>
        {quietOn && (
          <div className="flex items-center gap-2 pl-11">
            <Label htmlFor="quiet-from" className="text-[11px] text-neutral-500">Van</Label>
            <Input id="quiet-from" type="time" value={quietFrom}
              onChange={e => { setQuietFrom(e.target.value); setDirty(true); }}
              className="h-8 w-24" data-testid="push-quiet-from" />
            <Label htmlFor="quiet-to" className="text-[11px] text-neutral-500">tot</Label>
            <Input id="quiet-to" type="time" value={quietTo}
              onChange={e => { setQuietTo(e.target.value); setDirty(true); }}
              className="h-8 w-24" data-testid="push-quiet-to" />
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-neutral-100 flex items-center justify-between">
        <div className="text-[11px] text-neutral-500">
          {dirty ? "Wijzigingen zijn nog niet opgeslagen." : "Voorkeuren zijn actueel."}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          className="px-3 py-1.5 rounded bg-[color:var(--sage)] text-white text-xs font-medium disabled:opacity-40"
          data-testid="push-prefs-save"
        >
          {busy ? "Opslaan…" : "Opslaan"}
        </button>
      </div>
    </div>
  );
}
